import React, { useEffect, useRef, useState, useCallback } from "react";
import { Chip, TextField } from "@material-ui/core";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => {
    const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
    return {
        root: {
            "& .MuiOutlinedInput-root": {
                borderRadius: 12,
                fontSize: 13,
                paddingTop: 4,
                paddingBottom: 4,
            },
            "& .MuiAutocomplete-tag": {
                borderRadius: 50,
                fontWeight: 600,
                fontSize: "0.78em",
                height: 22,
            },
        },
        paper: {
            borderRadius: 12,
            boxShadow: "0 10px 36px rgba(0,0,0,0.14)",
            border: isDark
                ? "1px solid rgba(255,255,255,0.08)"
                : "1px solid rgba(0,0,0,0.06)",
            background: isDark ? "#121212" : "#ffffff",
            "& .MuiAutocomplete-option": {
                borderRadius: 7,
                margin: 4,
                fontSize: 14,
                "&[data-focus='true'], &:hover": {
                    background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                },
                "&[aria-selected='true']": {
                    background: "rgba(6, 207, 156, 0.10)",
                },
            },
        },
    };
});

const filter = createFilterOptions();

export function TagsContainer({ contact }) {
    const classes = useStyles();
    const [tags, setTags] = useState([]);
    const [selecteds, setSelecteds] = useState([]);
    const isMounted = useRef(true);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const loadContactTags = useCallback(async () => {
        if (!contact?.id) return;
        try {
            const { data } = await api.get(`/contacts/${contact.id}`);
            if (isMounted.current && Array.isArray(data.tags)) {
                setSelecteds(data.tags);
            }
        } catch (err) {
            if (Array.isArray(contact.tags)) {
                setSelecteds(contact.tags);
            }
        }
    }, [contact?.id]);

    const loadTags = async () => {
        try {
            const { data } = await api.get(`/tags/list`, { params: { kanban: 0 } });
            if (isMounted.current) setTags(data);
        } catch (err) {
            toastError(err);
        }
    };

    useEffect(() => {
        loadTags();
        loadContactTags();
    }, [contact?.id, loadContactTags]);

    const syncTags = async (next) => {
        try {
            await api.post(`/tags/sync`, { contactId: contact.id, tags: next });
        } catch (err) {
            toastError(err);
        }
    };

    const createTag = async (name) => {
        const trimmed = (name || "").trim();
        if (!trimmed) return null;
        try {
            const red = Math.floor(Math.random() * 256);
            const green = Math.floor(Math.random() * 256);
            const blue = Math.floor(Math.random() * 256);
            const color = `#${red.toString(16).padStart(2, "0")}${green
                .toString(16)
                .padStart(2, "0")}${blue.toString(16).padStart(2, "0")}`;
            const { data } = await api.post(`/tags`, {
                name: trimmed,
                kanban: 0,
                color,
            });
            await loadTags();
            return data;
        } catch (err) {
            toastError(err);
            return null;
        }
    };

    const handleChange = async (_event, value) => {
        // value pode conter strings (digitadas / freeSolo) ou objetos custom { inputValue }
        const resolved = [];
        for (const item of value) {
            if (typeof item === "string") {
                const created = await createTag(item);
                if (created) resolved.push(created);
            } else if (item && item.inputValue) {
                const created = await createTag(item.inputValue);
                if (created) resolved.push(created);
            } else if (item) {
                resolved.push(item);
            }
        }
        // dedup por id
        const seen = new Set();
        const dedup = resolved.filter((t) => {
            if (seen.has(t.id)) return false;
            seen.add(t.id);
            return true;
        });
        setSelecteds(dedup);
        await syncTags(dedup);
    };

    return (
        <Autocomplete
            multiple
            freeSolo
            size="small"
            value={selecteds}
            options={tags}
            getOptionLabel={(option) => {
                if (typeof option === "string") return option;
                if (option.inputValue) return option.inputValue;
                return option.name || "";
            }}
            getOptionSelected={(option, value) =>
                option && value && option.id === value.id
            }
            filterOptions={(options, params) => {
                const filtered = filter(options, params);
                const input = (params.inputValue || "").trim();
                if (input.length >= 3 && !options.some((o) => o.name === input)) {
                    filtered.push({
                        inputValue: input,
                        name: `${i18n.t("tagsResponsive.placeholder")}: "${input}"`,
                        __isNew: true,
                    });
                }
                return filtered;
            }}
            onChange={handleChange}
            classes={{ root: classes.root, paper: classes.paper }}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                    <Chip
                        size="small"
                        label={option.name}
                        {...getTagProps({ index })}
                        style={{
                            backgroundColor: option.color || "#9e9e9e",
                            color: "#fff",
                            fontWeight: 600,
                            border: "none",
                        }}
                    />
                ))
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    label={i18n.t("contactModal.form.tags")}
                    placeholder={i18n.t("contactModal.form.tagsPlaceholder")}
                />
            )}
        />
    );
}
